import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { CamaraCapturaComponent } from '../../components/camara-captura'
import { BancoChipsComponent } from '../../components/banco-chips'
import { GaleriaFotosComponent } from '../../components/galeria-fotos'
import { MiniFotoBlobComponent } from '../../components/mini-foto-blob'
import { VisorImagenComponent } from '../../components/visor-imagen'
import { SesionCapturaService, type TelaGuardadaLocal } from '../../../application/sesion-captura'
import { BloqueoRecargaService } from '../../../application/bloqueo-recarga'
import {
  guardarProducto,
  actualizarProducto,
  contarPendientes,
  listarProductosLocalDeProveedor,
  siguienteNumeroProducto,
  eliminarProducto,
  eliminarTela,
} from '../../../application/casos-de-uso'
import type { ProductoPendiente } from '../../../infrastructure/offline/db'
import { arrayBufferABlob } from '../../../infrastructure/offline/db'
import { numeroDesdeTexto } from '../../../infrastructure/util/numero'
import {
  obtenerBancoColores,
  obtenerBancoTipos,
  obtenerBancoAtributos,
  agregarColorAlBanco,
  agregarTipoAlBanco,
  agregarAtributoAlBanco,
  eliminarColorDelBanco,
  eliminarTipoDelBanco,
  eliminarAtributoDelBanco,
  TIPOS_SEMILLA,
} from '../../../infrastructure/offline/preferencias'
import { leerTextoDeImagen, extraerIdMuestrario } from '../../../infrastructure/ocr/reconocimiento-texto'
import { EVENTO_SINCRONIZADO } from '../../../infrastructure/offline/sincronizador'
import { espacioLibreBajo } from '../../../infrastructure/util/almacenamiento'
import { etiquetaProveedor } from '../../../domain/entidades'

/** Coordinamos la captura movil de productos, telas, fotos, OCR y guardado offline. */
@Component({
  selector: 'app-captura-producto',
  standalone: true,
  imports: [
    FormsModule,
    CamaraCapturaComponent,
    BancoChipsComponent,
    GaleriaFotosComponent,
    MiniFotoBlobComponent,
    VisorImagenComponent,
  ],
  templateUrl: './captura-producto.html',
})
export class CapturaProductoComponent implements OnDestroy, OnInit {
  private readonly router = inject(Router)
  private readonly bloqueoRecarga = inject(BloqueoRecargaService)
  readonly sesion = inject(SesionCapturaService)

  private static readonly ID_BLOQUEO = 'captura-producto'

  readonly proveedorCodigo = this.sesion.proveedorCodigo
  /** "PROV-A-008 · Lucky", o solo el código si todavía no hay nombre. */
  readonly encabezadoProveedor = computed(() => etiquetaProveedor(this.sesion.proveedorCodigo() ?? '', this.sesion.proveedorNombre()))
  readonly codigoProductoActual = this.sesion.codigoProductoActual
  readonly productoNum = this.sesion.productoNum
  readonly telaNum = this.sesion.telaNum
  readonly fotosProducto = this.sesion.fotosProducto
  readonly telasDelProducto = this.sesion.telasDelProducto

  readonly pendientes = signal(0)
  readonly productosGuardados = signal<ProductoPendiente[]>([])
  /**
   * Foto previa por producto guardado, para que el usuario identifique cuál
   * editar sin tener que adivinar por el código (pedido desde la feria,
   * 2026-09-05). Prioridad: foto general del producto → foto de color/
   * estructura → primera foto de tela disponible.
   *
   * Se calcula acá (computed, no en el template) para no crear un Blob
   * nuevo — y por lo tanto un object URL nuevo en MiniFotoBlobComponent — en
   * cada ciclo de detección de cambios; solo se recalcula cuando cambia
   * `productosGuardados`.
   */
  readonly productosConFotoPrevia = computed(() =>
    this.productosGuardados().map((p) => ({ producto: p, fotoPrevia: this.fotoPreviaDe(p) })),
  )
  /** Aviso proactivo — ver docs/hallazgo-almacenamiento-fotos-offline.md. */
  readonly espacioBajo = signal(false)

  // --- Constructor de tela nueva ---
  readonly fotoFrente = signal<Blob | undefined>(undefined)
  readonly fotoReverso = signal<Blob | undefined>(undefined)
  readonly fotoFrentePreviaUrl = signal<string | undefined>(undefined)
  readonly fotoReversoPreviaUrl = signal<string | undefined>(undefined)
  readonly colores = signal<string[]>([])
  readonly tipo = signal<string[]>([])
  readonly idTela = signal('')
  readonly bancoColores = signal(obtenerBancoColores())
  readonly bancoTipos = signal(obtenerBancoTipos())
  readonly bancoAtributos = signal(obtenerBancoAtributos())
  readonly mensajeTela = signal('')
  readonly tiposSemilla = TIPOS_SEMILLA

  // --- Datos de producto ---
  readonly fob = signal('')
  readonly moq = signal('')
  readonly unidadesPorContenedor = signal('')
  readonly colorEstructura = signal('')
  readonly colorEstructuraFoto = signal<Blob | undefined>(undefined)
  readonly colorEstructuraFotoPreviaUrl = signal<string | undefined>(undefined)
  readonly mostrarCamaraEstructura = signal(false)
  readonly notas = signal('')
  readonly atributos = signal<string[]>([])
  readonly editandoTelaId = signal<string | null>(null)
  readonly mensajeProducto = signal('')

  /**
   * Mismo criterio que ya usaba `iniciarEdicion` para bloquear "editar otro
   * producto mientras hay uno a medias" — lo subimos a computed de la clase
   * para reutilizarlo tambien como fuente de BloqueoRecargaService (ver
   * constructor). Todo esto vive solo en memoria hasta guardar; un reload
   * automatico en medio de esto lo pierde sin poder recuperarlo.
   */
  readonly hayTrabajoSinGuardar = computed(
    () =>
      this.fotosProducto().length > 0 ||
      this.telasDelProducto().length > 0 ||
      Boolean(this.fotoFrente()) ||
      this.colores().length > 0 ||
      this.fob().trim() !== '' ||
      this.moq().trim() !== '' ||
      this.unidadesPorContenedor().trim() !== '' ||
      this.colorEstructura().trim() !== '' ||
      this.notas().trim() !== '' ||
      this.atributos().length > 0,
  )

  // --- Detalle / edición de un producto ya guardado ---
  readonly detalleProducto = signal<ProductoPendiente | null>(null)
  /**
   * Bug preexistente (confirmado con `git blame`, viene del commit original
   * del proyecto, no de esta sesión): el modal de detalle le pasaba el
   * ArrayBuffer crudo a <app-mini-foto-blob>, que espera un Blob real —
   * URL.createObjectURL() no acepta un ArrayBuffer ("Overload resolution
   * failed"). Se precalculan los Blob acá (computed, no en el template) para
   * no crear uno nuevo en cada ciclo de detección de cambios — mismo
   * criterio que ya usamos en productosConFotoPrevia.
   */
  readonly detalleFotosBlobs = computed(() => {
    const dp = this.detalleProducto()
    return dp ? dp.fotosBlobs.map((f) => ({ ...f, blobReal: arrayBufferABlob(f.blob) })) : []
  })
  /** Mismo caso que detalleFotosBlobs, para la foto de color/estructura del modal de detalle. */
  readonly detalleColorEstructuraBlob = computed(() => {
    const dp = this.detalleProducto()
    return dp?.colorEstructuraFotoBlob ? arrayBufferABlob(dp.colorEstructuraFotoBlob) : undefined
  })
  /** Mismo caso que detalleFotosBlobs, para las fotos de frente/reverso de cada tela. */
  readonly detalleTelasBlobs = computed(() => {
    const dp = this.detalleProducto()
    return dp
      ? dp.telas.map((t) => ({
          ...t,
          fotoFrenteBlobReal: t.fotoFrenteBlob ? arrayBufferABlob(t.fotoFrenteBlob) : undefined,
          fotoReversoBlobReal: t.fotoReversoBlob ? arrayBufferABlob(t.fotoReversoBlob) : undefined,
        }))
      : []
  })
  // id de Dexie (no productId) del producto que se está corrigiendo — null = producto nuevo.
  readonly editandoDexieId = signal<number | null>(null)

  // --- OCR offline (Tesseract.js) para sugerir el ID de muestrario ---
  readonly leyendoIdMuestrario = signal(false)
  // Guardamos el valor vigente para que el OCR tardio no pise escritura del usuario.
  private idTelaActual = ''
  // Invalidamos resultados OCR cuando el constructor cambia de contexto.
  private tokenBuilder = 0

  private ultimoProveedorCodigo: string | null = null
  private ultimoProductoNum: number | null = null

  private readonly alVolverSenal = () => void contarPendientes().then((n) => this.pendientes.set(n))
  private readonly alTerminarSync = () => void contarPendientes().then((n) => this.pendientes.set(n))

  constructor() {
    effect(() => { this.idTelaActual = this.idTela() })

    effect(() => {
      if (!this.proveedorCodigo()) void this.router.navigateByUrl('/movil')
    })

    // Mostramos lo ya guardado localmente para el proveedor activo.
    effect(() => {
      const codigo = this.proveedorCodigo()
      const num = this.productoNum()
      if (!codigo) return
      if (codigo === this.ultimoProveedorCodigo && num === this.ultimoProductoNum) return
      this.ultimoProveedorCodigo = codigo
      this.ultimoProductoNum = num
      void listarProductosLocalDeProveedor(codigo).then((ps) => this.productosGuardados.set(ps))
    })

    effect((onCleanup) => {
      const blob = this.fotoFrente()
      if (!blob) { this.fotoFrentePreviaUrl.set(undefined); return }
      const url = URL.createObjectURL(blob)
      this.fotoFrentePreviaUrl.set(url)
      onCleanup(() => URL.revokeObjectURL(url))
    })

    effect((onCleanup) => {
      const blob = this.fotoReverso()
      if (!blob) { this.fotoReversoPreviaUrl.set(undefined); return }
      const url = URL.createObjectURL(blob)
      this.fotoReversoPreviaUrl.set(url)
      onCleanup(() => URL.revokeObjectURL(url))
    })

    effect((onCleanup) => {
      const blob = this.colorEstructuraFoto()
      if (!blob) { this.colorEstructuraFotoPreviaUrl.set(undefined); return }
      const url = URL.createObjectURL(blob)
      this.colorEstructuraFotoPreviaUrl.set(url)
      onCleanup(() => URL.revokeObjectURL(url))
    })
  }

  ngOnInit(): void {
    void contarPendientes().then((n) => this.pendientes.set(n))
    window.addEventListener('online', this.alVolverSenal)
    window.addEventListener(EVENTO_SINCRONIZADO, this.alTerminarSync)
    this.bloqueoRecarga.registrar(CapturaProductoComponent.ID_BLOQUEO, this.hayTrabajoSinGuardar)
    void espacioLibreBajo().then((bajo) => this.espacioBajo.set(bajo))
  }

  limpiarBuilderTela(): void {
    this.tokenBuilder += 1
    this.fotoFrente.set(undefined)
    this.fotoReverso.set(undefined)
    this.colores.set([])
    this.tipo.set([])
    this.idTela.set('')
    this.mensajeTela.set('')
    this.leyendoIdMuestrario.set(false)
  }

  async alCapturarFrenteTela(blob: Blob): Promise<void> {
    this.fotoFrente.set(blob)
    // Sugerimos ID solo si el campo sigue vacio y no estamos editando.
    if (this.idTelaActual.trim() !== '' || this.editandoTelaId()) return
    const token = this.tokenBuilder
    this.leyendoIdMuestrario.set(true)
    try {
      const texto = await leerTextoDeImagen(blob)
      const candidato = extraerIdMuestrario(texto)
      if (candidato && this.tokenBuilder === token && this.idTelaActual.trim() === '') {
        this.idTela.set(candidato)
      }
    } catch {
      // El OCR es opcional; si falla, el usuario puede escribir el dato.
    } finally {
      if (this.tokenBuilder === token) this.leyendoIdMuestrario.set(false)
    }
  }

  guardarTelaLista(): void {
    if (this.colores().length === 0) { this.mensajeTela.set('⚠ falta el color de la tela'); return }
    if (!this.fotoFrente() && !this.editandoTelaId()) { this.mensajeTela.set('⚠ falta la foto del frente'); return }

    const editando = this.editandoTelaId()
    const id = editando ?? `${this.codigoProductoActual()}-T${String(this.telaNum()).padStart(2, '0')}`
    const tela: TelaGuardadaLocal = {
      id,
      muestrario: this.idTela().trim(),
      colores: this.colores(),
      tipo: this.tipo()[0] ?? null,
      fotoFrenteBlob: this.fotoFrente(),
      fotoReversoBlob: this.fotoReverso(),
    }

    if (editando) {
      this.sesion.editarTela(editando, tela)
      this.editandoTelaId.set(null)
    } else {
      this.sesion.agregarTela(tela)
    }
    this.limpiarBuilderTela()
    this.mensajeTela.set('✓ Tela guardada')
    setTimeout(() => this.mensajeTela.set(''), 1500)
  }

  editarTela(tela: TelaGuardadaLocal): void {
    this.tokenBuilder += 1 // Descarta cualquier OCR pendiente del contexto anterior.
    this.editandoTelaId.set(tela.id)
    this.colores.set(tela.colores)
    this.tipo.set(tela.tipo ? [tela.tipo] : [])
    this.idTela.set(tela.muestrario)
    this.fotoFrente.set(tela.fotoFrenteBlob)
    this.fotoReverso.set(tela.fotoReversoBlob)
    document.getElementById('constructor-tela')?.scrollIntoView({ behavior: 'smooth' })
  }

  alAgregarColor(nombre: string): void {
    agregarColorAlBanco(nombre)
    this.bancoColores.set(obtenerBancoColores())
  }
  alEliminarColor(nombre: string): void {
    eliminarColorDelBanco(nombre)
    this.bancoColores.set(obtenerBancoColores())
    this.colores.update((cs) => cs.filter((c) => c !== nombre))
  }
  alAgregarTipo(nombre: string): void {
    agregarTipoAlBanco(nombre)
    this.bancoTipos.set(obtenerBancoTipos())
  }
  alEliminarTipo(nombre: string): void {
    eliminarTipoDelBanco(nombre)
    this.bancoTipos.set(obtenerBancoTipos())
    this.tipo.update((ts) => ts.filter((t) => t !== nombre))
  }
  alAgregarAtributo(nombre: string): void {
    agregarAtributoAlBanco(nombre)
    this.bancoAtributos.set(obtenerBancoAtributos())
  }
  alEliminarAtributo(nombre: string): void {
    eliminarAtributoDelBanco(nombre)
    this.bancoAtributos.set(obtenerBancoAtributos())
    this.atributos.update((as) => as.filter((a) => a !== nombre))
  }

  abrirDetalle(p: ProductoPendiente): void {
    this.detalleProducto.set(p)
  }

  /**
   * Reset del formulario de producto tras guardar (extraído de productoListo
   * para poder reusarlo también al eliminar el producto que se estaba
   * editando — ver confirmarBorrarProducto).
   */
  private async limpiarFormularioProducto(): Promise<void> {
    const siguienteNumero = await siguienteNumeroProducto(this.proveedorCodigo()!)
    this.sesion.iniciarProducto(siguienteNumero)
    this.editandoDexieId.set(null)
    this.fob.set('')
    this.moq.set('')
    this.unidadesPorContenedor.set('')
    this.colorEstructura.set('')
    this.colorEstructuraFoto.set(undefined)
    this.notas.set('')
    this.atributos.set([])
  }

  // --- Eliminar producto / tela (pedido desde la feria, 2026-09-05) ---
  readonly confirmandoBorrarProducto = signal(false)
  readonly eliminandoProducto = signal(false)

  pedirBorrarProducto(): void {
    this.confirmandoBorrarProducto.set(true)
  }

  cancelarBorrarProducto(): void {
    this.confirmandoBorrarProducto.set(false)
  }

  async confirmarBorrarProducto(): Promise<void> {
    const p = this.detalleProducto()
    if (!p?.id) return
    this.eliminandoProducto.set(true)
    try {
      await eliminarProducto(p.id)
      // Si justo se estaba editando ESTE producto en el constructor de abajo,
      // limpiamos el formulario también — sin esto, "Guardar cambios" seguiría
      // habilitado apuntando a un id que ya no existe en Dexie (el update
      // simplemente no encontraría filas, sin error, pero el usuario creería
      // que guardó algo que en realidad se perdió).
      if (this.editandoDexieId() === p.id) await this.limpiarFormularioProducto()
      this.confirmandoBorrarProducto.set(false)
      this.detalleProducto.set(null)
      await listarProductosLocalDeProveedor(this.proveedorCodigo()!).then((ps) => this.productosGuardados.set(ps))
    } finally {
      this.eliminandoProducto.set(false)
    }
  }

  readonly borrandoTelaId = signal<string | null>(null)
  readonly eliminandoTela = signal(false)

  pedirBorrarTela(id: string): void {
    this.borrandoTelaId.set(id)
  }

  cancelarBorrarTela(): void {
    this.borrandoTelaId.set(null)
  }

  /**
   * Solo hay fila de Dexie que actualizar remotamente si estamos editando un
   * producto ya guardado (editandoDexieId set) — si la tela es de un
   * producto todavía en construcción (nunca guardado), no hay nada remoto
   * que limpiar: alcanza con quitarla de la sesión en memoria.
   */
  async confirmarBorrarTela(): Promise<void> {
    const id = this.borrandoTelaId()
    if (!id) return
    this.eliminandoTela.set(true)
    try {
      const idProducto = this.editandoDexieId()
      if (idProducto != null) await eliminarTela(idProducto, id)
      this.sesion.eliminarTela(id)
      this.borrandoTelaId.set(null)
    } finally {
      this.eliminandoTela.set(false)
    }
  }

  /**
   * Elige la mejor foto disponible localmente para representar un producto
   * ya guardado. Si el producto lleva más de 72h sincronizado, su blob local
   * ya se limpió (ver limpiarBlobsSincronizadosAntiguos en db.ts) — en ese
   * caso no hay nada que mostrar, MiniFotoBlobComponent ya maneja bien un
   * blob vacío (no muestra nada, no rompe el layout).
   */
  private fotoPreviaDe(p: ProductoPendiente): Blob | undefined {
    const buffer =
      p.fotosBlobs[0]?.blob ??
      p.colorEstructuraFotoBlob ??
      p.telas.find((t) => t.fotoFrenteBlob)?.fotoFrenteBlob ??
      p.telas.find((t) => t.fotoReversoBlob)?.fotoReversoBlob
    return buffer ? arrayBufferABlob(buffer) : undefined
  }

  /**
   * Visor a pantalla completa para la miniatura de "productos ya guardados"
   * — mismo componente que usa el dashboard (VisorImagenComponent), 100%
   * local: no hay red ni IndexedDB de por medio, solo una URL temporal sobre
   * el blob que ya está en memoria (se libera al cerrar).
   */
  readonly imagenAmpliada = signal<{ url: string; titulo: string } | null>(null)
  private urlImagenAmpliadaActual?: string

  abrirImagenPrevia(evento: Event, blob: Blob | undefined, titulo: string): void {
    evento.stopPropagation() // no disparar tambien abrirDetalle() del producto
    if (!blob) return
    if (this.urlImagenAmpliadaActual) URL.revokeObjectURL(this.urlImagenAmpliadaActual)
    this.urlImagenAmpliadaActual = URL.createObjectURL(blob)
    this.imagenAmpliada.set({ url: this.urlImagenAmpliadaActual, titulo })
  }

  cerrarImagenAmpliada(): void {
    if (this.urlImagenAmpliadaActual) URL.revokeObjectURL(this.urlImagenAmpliadaActual)
    this.urlImagenAmpliadaActual = undefined
    this.imagenAmpliada.set(null)
  }

  cerrarDetalle(): void {
    this.detalleProducto.set(null)
  }

  iniciarEdicion(p: ProductoPendiente): void {
    // Bloqueamos edicion si existe trabajo sin guardar en el producto actual.
    if (this.hayTrabajoSinGuardar()) {
      this.detalleProducto.set(null)
      this.mensajeProducto.set('⚠ Termina o descarta el producto en curso antes de editar uno ya guardado.')
      return
    }

    const numero = Number(p.productoId.split('-P').pop()) || this.productoNum()
    this.sesion.entrarModoEdicion(
      p.productoId,
      numero,
      // Reconstruimos Blob desde el ArrayBuffer persistido solo para mostrarlo/editarlo.
      p.fotosBlobs.map((f) => ({ id: f.id, blob: arrayBufferABlob(f.blob), etiqueta: f.etiqueta })),
      p.telas.map((t) => ({
        ...t,
        fotoFrenteBlob: t.fotoFrenteBlob ? arrayBufferABlob(t.fotoFrenteBlob) : undefined,
        fotoReversoBlob: t.fotoReversoBlob ? arrayBufferABlob(t.fotoReversoBlob) : undefined,
      })),
    )
    this.editandoDexieId.set(p.id ?? null)
    this.fob.set(p.fob !== undefined ? String(p.fob) : '')
    this.moq.set(p.moq ?? '')
    this.unidadesPorContenedor.set(p.unidadesPorContenedor ?? '')
    this.colorEstructura.set(p.colorEstructura ?? '')
    this.colorEstructuraFoto.set(p.colorEstructuraFotoBlob ? arrayBufferABlob(p.colorEstructuraFotoBlob) : undefined)
    this.notas.set(p.notas)
    this.atributos.set(p.atributosEspeciales)
    this.detalleProducto.set(null)
    this.mensajeProducto.set('')
  }

  async productoListo(): Promise<void> {
    this.mensajeProducto.set('')
    // Aceptamos una foto general o una foto de tela para completar el producto.
    const hayAlgunaFoto = this.fotosProducto().length > 0 || this.telasDelProducto().some((t) => t.fotoFrenteBlob)
    if (!hayAlgunaFoto) {
      this.mensajeProducto.set('⚠ Falta al menos una foto — del producto (arriba) o de alguna tela.')
      return
    }
    if (this.fotoFrente() || this.colores().length > 0) {
      this.mensajeProducto.set("⚠ Hay una tela a medias en el constructor. Toca 'Tela lista' o borra esos datos antes de continuar.")
      return
    }

    const fobVal = this.fob()
    const datos = {
      productoId: this.codigoProductoActual()!,
      proveedorCodigo: this.proveedorCodigo()!,
      mueble: '',
      categoria: '',
      fob: numeroDesdeTexto(fobVal),
      moq: this.moq(),
      unidadesPorContenedor: this.unidadesPorContenedor(),
      colorEstructura: this.colorEstructura(),
      colorEstructuraFotoBlob: this.colorEstructuraFoto(),
      notas: this.notas(),
      atributosEspeciales: this.atributos(),
      fotosBlobs: this.fotosProducto(),
      telas: this.telasDelProducto(),
    }

    const idEdicion = this.editandoDexieId()
    const fueEdicion = idEdicion != null
    try {
      if (fueEdicion) {
        await actualizarProducto(idEdicion, datos)
      } else {
        await guardarProducto(datos)
      }
    } catch (err) {
      // Incluye el caso de cuota llena (ver casos-de-uso.ts) — sin este catch,
      // el error quedaba sin capturar y la persona no se enteraba de nada.
      this.mensajeProducto.set(`⚠ ${err instanceof Error ? err.message : 'No se pudo guardar el producto.'}`)
      return
    } finally {
      void espacioLibreBajo().then((bajo) => this.espacioBajo.set(bajo))
    }

    await this.limpiarFormularioProducto()
    this.mensajeProducto.set(fueEdicion ? '✓ Cambios guardados.' : '✓ Producto guardado — sigue con el siguiente.')
    setTimeout(() => this.mensajeProducto.set(''), 2500)
  }

  /**
   * Botón "←" del encabezado — pedido desde la feria, 2026-09-06: antes solo
   * se podía volver al listado bajando hasta "Terminar proveedor". A
   * diferencia de ese botón (que bloquea con un mensaje si hay algo a medias),
   * este solo pregunta y deja decidir — nunca intenta guardar, así que nunca
   * puede fallar por validación (ej. "falta una foto"): si hay algo sin
   * guardar, cualquier cosa (producto o tela a medias, mismo chequeo que ya
   * usa hayTrabajoSinGuardar), se pierde solo si la persona confirma.
   */
  readonly confirmandoVolver = signal(false)

  async volverAlListado(): Promise<void> {
    if (this.hayTrabajoSinGuardar()) {
      this.confirmandoVolver.set(true)
      return
    }
    this.sesion.limpiarProveedor()
    await this.router.navigateByUrl('/movil')
  }

  cancelarVolver(): void {
    this.confirmandoVolver.set(false)
  }

  async confirmarVolverSinGuardar(): Promise<void> {
    this.confirmandoVolver.set(false)
    this.sesion.limpiarProveedor()
    await this.router.navigateByUrl('/movil')
  }

  terminarProveedor(): void {
    if (this.fotosProducto().length > 0 || this.fotoFrente() || this.colores().length > 0) {
      this.mensajeProducto.set('⚠ Hay un producto o tela a medias. Guarda o descarta antes de terminar.')
      return
    }
    this.sesion.limpiarProveedor()
    void this.router.navigateByUrl('/movil')
  }

  claseMensajeProducto(mensaje: string): string {
    const base = 'text-[12.5px] font-semibold px-3.5 py-2.5 rounded-lg '
    return base + (mensaje.startsWith('✓') ? 'text-[var(--sage-ink)] bg-[var(--sage-soft)]' : 'text-[var(--coral-ink)] bg-[var(--coral-soft)]')
  }

  alAgregarFotoProducto(e: { blob: Blob; etiqueta?: string }): void {
    this.sesion.agregarFotoProducto(e.blob, e.etiqueta)
  }
  alRenombrarFotoProducto(e: { id: string; etiqueta: string }): void {
    this.sesion.renombrarFotoProducto(e.id, e.etiqueta)
  }
  alEliminarFotoProducto(id: string): void {
    this.sesion.eliminarFotoProducto(id)
  }

  alCapturarColorEstructura(blob: Blob): void {
    this.colorEstructuraFoto.set(blob)
    this.mostrarCamaraEstructura.set(false)
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.alVolverSenal)
    window.removeEventListener(EVENTO_SINCRONIZADO, this.alTerminarSync)
    this.bloqueoRecarga.desregistrar(CapturaProductoComponent.ID_BLOQUEO)
    if (this.urlImagenAmpliadaActual) URL.revokeObjectURL(this.urlImagenAmpliadaActual)
  }
}
